import React, { useState, useEffect } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Navbar2 from './Navbar';
import Cookies from 'universal-cookie/es6';
import { Redirect } from 'react-router-dom';

const api = process.env.REACT_APP_API;

const MESES = [
  { value: '01', label: 'Enero' },
  { value: '02', label: 'Febrero' },
  { value: '03', label: 'Marzo' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Mayo' },
  { value: '06', label: 'Junio' },
  { value: '07', label: 'Julio' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

const ESTADOS = ['Todos', 'Aceptada', 'Rechazada', 'Cancelada'];

const currentYear = new Date().getFullYear();
const AÑOS = Array.from({ length: 6 }, (_, i) => currentYear - 5 + i + 1).reverse();

export default function Reporte() {
  const cookies = new Cookies();

  const normalizeRole = (value) => {
    if (value === undefined || value === null) return '';
    const raw = String(value);
    try {
      return decodeURIComponent(raw)
        .replace(/^['\"]|['\"]$/g, '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    } catch (e) {
      return raw
        .replace(/^['\"]|['\"]$/g, '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    }
  };

  const rol = normalizeRole(cookies.get('nombre_Rol'));
  const idRol = String(cookies.get('id_rol') || '').trim();
  const isBasicUser = rol.includes('usuario') || idRol === '1';

  const [datos, setDatos] = useState([]);
  const [filtrados, setFiltrados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [cargado, setCargado] = useState(false);

  // Filtros
  const [tipoFiltro, setTipoFiltro] = useState('mes'); // mes | año | rango | todo
  const [mes, setMes] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [año, setAño] = useState(String(currentYear));
  const [añoSolo, setAñoSolo] = useState(String(currentYear));
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [estado, setEstado] = useState('Todos');
  const [edificio, setEdificio] = useState('Todos');
  const [edificios, setEdificios] = useState([]);

  // Paginación
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(10);

  // Cargar datos al montar
  useEffect(() => {
    cargarDatos();
  }, []); // eslint-disable-line

  // Calcular edificios únicos cuando llegan datos
  useEffect(() => {
    if (datos.length > 0) {
      const unicos = ['Todos', ...new Set(datos.map(d => d['Edificio']).filter(Boolean))];
      setEdificios(unicos);
    }
  }, [datos]);

  // Aplicar filtros cuando cambia algo
  useEffect(() => {
    aplicarFiltros();
    setPagina(1); // reset página al cambiar filtros
  }, [datos, tipoFiltro, mes, año, añoSolo, fechaDesde, fechaHasta, estado, edificio]); // eslint-disable-line

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const res = await fetch(`${api}/generarReporte`);
      const data = await res.json();
      data.forEach(r => {
        r['Folio'] = String(r['Folio']).startsWith('RS') ? r['Folio'] : `RS${r['Folio']}`;
        r['dia'] = r['dia'] ? r['dia'].slice(0, 10) : '';
        r['Fecha Respuesta'] = r['Fecha Respuesta'] ? r['Fecha Respuesta'].slice(0, 10) : '';
      });
      setDatos(data);
      setCargado(true);
    } catch (e) {
      console.error(e);
    }
    setCargando(false);
  };

  const aplicarFiltros = () => {
    let resultado = [...datos];

    // Filtro de fecha
    if (tipoFiltro === 'mes') {
      resultado = resultado.filter(r => {
        if (!r['dia']) return false;
        return r['dia'].startsWith(`${año}-${mes}`);
      });
    } else if (tipoFiltro === 'año') {
      resultado = resultado.filter(r => {
        if (!r['dia']) return false;
        return r['dia'].startsWith(añoSolo);
      });
    } else if (tipoFiltro === 'rango') {
      resultado = resultado.filter(r => {
        if (!r['dia']) return false;
        if (fechaDesde && r['dia'] < fechaDesde) return false;
        if (fechaHasta && r['dia'] > fechaHasta) return false;
        return true;
      });
    }

    // Filtro de estado
    if (estado !== 'Todos') {
      resultado = resultado.filter(r => r['Estado'] && r['Estado'].toLowerCase() === estado.toLowerCase());
    }

    // Filtro de edificio
    if (edificio !== 'Todos') {
      resultado = resultado.filter(r => r['Edificio'] === edificio);
    }

    setFiltrados(resultado);
  };

  const descargarExcel = async () => {
    if (filtrados.length === 0) return;

    const nombreArchivo = tipoFiltro === 'mes'
      ? `Reporte_${MESES.find(m => m.value === mes)?.label}_${año}.xlsx`
      : tipoFiltro === 'año'
      ? `Reporte_${añoSolo}.xlsx`
      : tipoFiltro === 'rango'
      ? `Reporte_${fechaDesde}_al_${fechaHasta}.xlsx`
      : `Reporte_Todo.xlsx`;

    const wb = new ExcelJS.Workbook();
    wb.creator = 'Reserva de Salas';
    wb.created = new Date();

    const ws = wb.addWorksheet('Reporte', { views: [{ state: 'frozen', ySplit: 3 }] });

    // ── Fila 1: Título del reporte ──────────────────────────────────
    ws.mergeCells('A1:I1');
    const titleCell = ws.getCell('A1');
    titleCell.value = `Reporte de Reservas — ${nombreArchivo.replace('.xlsx','').replace(/_/g,' ')}`;
    titleCell.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A5F' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getRow(1).height = 30;

    // ── Fila 2: Subtítulo con totales ───────────────────────────────
    ws.mergeCells('A2:I2');
    const subCell = ws.getCell('A2');
    const aceptadas = filtrados.filter(r => r['Estado']?.toLowerCase() === 'aceptada').length;
    const rechazadas = filtrados.filter(r => r['Estado']?.toLowerCase() === 'rechazada').length;
    const canceladas = filtrados.filter(r => r['Estado']?.toLowerCase() === 'cancelada').length;
    subCell.value = `Total: ${filtrados.length} registros   |   ✔ Aceptadas: ${aceptadas}   |   ✘ Rechazadas: ${rechazadas}   |   ✖ Canceladas: ${canceladas}`;
    subCell.font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FFFFFFFF' } };
    subCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2563EB' } };
    subCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getRow(2).height = 20;

    // ── Fila 3: Encabezados de columna ──────────────────────────────
    const headers = ['Folio','Edificio','Nivel','Nombre De Sala','Día','Hora Inicio','Fecha Respuesta','Estado','Comentario'];
    const campos   = ['Folio','Edificio','Nivel','Nombre De Sala','dia','hora_Inicio','Fecha Respuesta','Estado','Comentario'];
    const headerRow = ws.addRow(headers);
    headerRow.eachCell(cell => {
      cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF1E3A5F' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: false };
      cell.border = {
        top:    { style: 'thin', color: { argb: 'FF94A3B8' } },
        bottom: { style: 'medium', color: { argb: 'FF2563EB' } },
        left:   { style: 'thin', color: { argb: 'FFCBD5E1' } },
        right:  { style: 'thin', color: { argb: 'FFCBD5E1' } },
      };
    });
    headerRow.height = 22;

    // ── Filas de datos ──────────────────────────────────────────────
    const estadoColor = (est) => {
      if (!est) return null;
      const e = est.toLowerCase();
      if (e === 'aceptada')  return { bg: 'FFD1FAE5', fg: 'FF065F46' };
      if (e === 'rechazada') return { bg: 'FFFEE2E2', fg: 'FF991B1B' };
      if (e === 'cancelada') return { bg: 'FFFEE2E2', fg: 'FF991B1B' };
      return { bg: 'FFE0E7FF', fg: 'FF3730A3' };
    };

    filtrados.forEach((r, idx) => {
      const isEven = idx % 2 === 0;
      const rowBg = isEven ? 'FFFFFFFF' : 'FFF8FAFC';
      const dataRow = ws.addRow(campos.map(c => r[c] ?? ''));
      dataRow.height = 18;
      dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.font = { name: 'Calibri', size: 10 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
        cell.alignment = { vertical: 'middle', horizontal: colNumber === 9 ? 'left' : 'center' };
        cell.border = {
          top:    { style: 'hair', color: { argb: 'FFE2E8F0' } },
          bottom: { style: 'hair', color: { argb: 'FFE2E8F0' } },
          left:   { style: 'thin', color: { argb: 'FFCBD5E1' } },
          right:  { style: 'thin', color: { argb: 'FFCBD5E1' } },
        };
        // Colorear celda Estado
        if (colNumber === 8) {
          const c2 = estadoColor(r['Estado']);
          if (c2) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: c2.bg } };
            cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: c2.fg } };
          }
        }
      });
    });

    // ── Anchos de columna ───────────────────────────────────────────
    const widths = [14, 16, 10, 22, 14, 14, 18, 14, 35];
    widths.forEach((w, i) => { ws.getColumn(i + 1).width = w; });

    // ── Descargar ───────────────────────────────────────────────────
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), nombreArchivo);
  };

  const badgeColor = (est) => {
    if (!est) return {};
    const e = est.toLowerCase();
    if (e === 'aceptada') return { background: '#d1fae5', color: '#065f46' };
    if (e === 'rechazada' || e === 'cancelada') return { background: '#fee2e2', color: '#991b1b' };
    return { background: '#e0e7ff', color: '#3730a3' };
  };

  if (isBasicUser) return <Redirect to="/inicio" />;

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", background: '#eef2f7', minHeight: '100vh' }}>
      <Navbar2 />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 16px' }}>

        {/* ── Encabezado ── */}
        <div style={{
          background: 'linear-gradient(90deg, #1e3a5f 0%, #2563eb 100%)',
          borderRadius: 14, padding: '24px 28px', marginBottom: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <h2 style={{ color: '#fff', margin: 0, fontWeight: 800, fontSize: 22 }}>📊 Generador de Reportes</h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', margin: '4px 0 0', fontSize: 13 }}>
              Filtra y descarga el reporte en Excel según los criterios que necesites.
            </p>
          </div>
          <button
            onClick={descargarExcel}
            disabled={filtrados.length === 0}
            style={{
              background: filtrados.length === 0 ? '#94a3b8' : '#22c55e',
              color: '#fff', border: 'none', borderRadius: 10,
              padding: '10px 24px', cursor: filtrados.length === 0 ? 'not-allowed' : 'pointer',
              fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8,
              transition: 'background 0.2s',
            }}
          >
            ⬇️ Descargar Excel {filtrados.length > 0 && `(${filtrados.length} registros)`}
          </button>
        </div>

        {/* ── Filtros ── */}
        <div style={{
          background: '#fff', borderRadius: 14,
          boxShadow: '0 3px 16px rgba(0,0,0,0.07)', padding: 24, marginBottom: 24,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1e3a5f', marginBottom: 16, borderLeft: '4px solid #3b82f6', paddingLeft: 10 }}>
            Filtros
          </div>

          {/* Tipo de filtro tabs */}
          <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 10, padding: 4, marginBottom: 20, width: 'fit-content' }}>
            {[['mes', '📅 Por Mes'], ['año', '📆 Por Año'], ['rango', '📋 Rango de Fechas'], ['todo', '📂 Todo']].map(([v, label]) => (
              <button key={v}
                onClick={() => setTipoFiltro(v)}
                style={{
                  padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600,
                  background: tipoFiltro === v ? '#3b82f6' : 'transparent',
                  color: tipoFiltro === v ? '#fff' : '#64748b',
                  transition: 'all 0.18s',
                }}
              >{label}</button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>

            {/* Filtros de fecha según tipo */}
            {tipoFiltro === 'mes' && (
              <>
                <div>
                  <label style={lblStyle}>Mes</label>
                  <select value={mes} onChange={e => setMes(e.target.value)} style={selStyle}>
                    {MESES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lblStyle}>Año</label>
                  <select value={año} onChange={e => setAño(e.target.value)} style={selStyle}>
                    {AÑOS.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </>
            )}

            {tipoFiltro === 'año' && (
              <div>
                <label style={lblStyle}>Año</label>
                <select value={añoSolo} onChange={e => setAñoSolo(e.target.value)} style={selStyle}>
                  {AÑOS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            )}

            {tipoFiltro === 'rango' && (
              <>
                <div>
                  <label style={lblStyle}>Desde</label>
                  <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={lblStyle}>Hasta</label>
                  <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} style={inputStyle} />
                </div>
              </>
            )}

            {/* Filtro por estado */}
            <div>
              <label style={lblStyle}>Estado</label>
              <select value={estado} onChange={e => setEstado(e.target.value)} style={selStyle}>
                {ESTADOS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Filtro por edificio */}
            <div>
              <label style={lblStyle}>Edificio</label>
              <select value={edificio} onChange={e => setEdificio(e.target.value)} style={selStyle}>
                {edificios.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>

            <button
              onClick={cargarDatos}
              style={{
                background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8,
                padding: '8px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 13, height: 38,
              }}
            >🔄 Actualizar datos</button>
          </div>
        </div>

        {/* ── Tabla de previsualización ── */}
        <div style={{
          background: '#fff', borderRadius: 14,
          boxShadow: '0 3px 16px rgba(0,0,0,0.07)', padding: 24,
        }}>
          {/* Barra superior: título + selector de filas por página */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1e3a5f', borderLeft: '4px solid #3b82f6', paddingLeft: 10 }}>
              Vista previa — {cargando ? 'Cargando…' : `${filtrados.length} registros`}
            </div>
            {filtrados.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span style={{ color: '#64748b', fontWeight: 600 }}>Filas por página:</span>
                <select
                  value={porPagina}
                  onChange={e => { setPorPagina(Number(e.target.value)); setPagina(1); }}
                  style={{ ...selStyle, minWidth: 80 }}
                >
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}
          </div>

          {!cargado ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Cargando datos…</div>
          ) : filtrados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 14 }}>
              No hay registros para los filtros seleccionados.
            </div>
          ) : (() => {
            const totalPaginas = Math.ceil(filtrados.length / porPagina);
            const inicio = (pagina - 1) * porPagina;
            const paginaActual = filtrados.slice(inicio, inicio + porPagina);
            // Generar rango de páginas a mostrar (máx 5 botones)
            const delta = 2;
            const rango = [];
            for (let i = Math.max(1, pagina - delta); i <= Math.min(totalPaginas, pagina + delta); i++) rango.push(i);

            return (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9' }}>
                        {['Folio', 'Edificio', 'Nivel', 'Sala', 'Día', 'Hora Inicio', 'Fecha Respuesta', 'Estado', 'Comentario'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#1e3a5f', fontWeight: 700, whiteSpace: 'nowrap', borderBottom: '2px solid #e2e8f0' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginaActual.map((r, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={tdStyle}>{r['Folio']}</td>
                          <td style={tdStyle}>{r['Edificio']}</td>
                          <td style={tdStyle}>{r['Nivel']}</td>
                          <td style={tdStyle}>{r['Nombre De Sala']}</td>
                          <td style={tdStyle}>{r['dia']}</td>
                          <td style={tdStyle}>{r['hora_Inicio']}</td>
                          <td style={tdStyle}>{r['Fecha Respuesta']}</td>
                          <td style={tdStyle}>
                            <span style={{ ...badgeColor(r['Estado']), padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                              {r['Estado']}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r['Comentario']}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ── Controles de paginación ── */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, flexWrap: 'wrap', gap: 8 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>
                    Mostrando {inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}
                  </span>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <button onClick={() => setPagina(1)} disabled={pagina === 1} style={pgBtn(pagina === 1)}>«</button>
                    <button onClick={() => setPagina(p => p - 1)} disabled={pagina === 1} style={pgBtn(pagina === 1)}>‹</button>
                    {rango[0] > 1 && <span style={{ padding: '0 4px', color: '#94a3b8' }}>…</span>}
                    {rango.map(p => (
                      <button key={p} onClick={() => setPagina(p)} style={pgBtn(false, p === pagina)}>{p}</button>
                    ))}
                    {rango[rango.length - 1] < totalPaginas && <span style={{ padding: '0 4px', color: '#94a3b8' }}>…</span>}
                    <button onClick={() => setPagina(p => p + 1)} disabled={pagina === totalPaginas} style={pgBtn(pagina === totalPaginas)}>›</button>
                    <button onClick={() => setPagina(totalPaginas)} disabled={pagina === totalPaginas} style={pgBtn(pagina === totalPaginas)}>»</button>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

const lblStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 };
const selStyle = { padding: '8px 14px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: 13, color: '#1e3a5f', background: '#f8fafc', cursor: 'pointer', outline: 'none', minWidth: 150 };
const inputStyle = { padding: '8px 14px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: 13, color: '#1e3a5f', background: '#f8fafc', outline: 'none' };
const tdStyle = { padding: '9px 12px', color: '#374151' };
const pgBtn = (disabled, active = false) => ({
  minWidth: 32, height: 32, borderRadius: 7, border: active ? 'none' : '1.5px solid #e2e8f0',
  background: active ? '#3b82f6' : disabled ? '#f1f5f9' : '#fff',
  color: active ? '#fff' : disabled ? '#cbd5e1' : '#1e3a5f',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontWeight: 600, fontSize: 13, padding: '0 8px',
  transition: 'all 0.15s',
});
